import { getMonth, getQuarter, getYear, addDays, getDate } from 'date-fns';
import memoizeOne from 'memoize-one';
import type { ColumnDuration, TimelineDuration } from '../../../../../../model/timeline';
import { getCurrentYear } from '../../../../../../utils/dates.tsx';
import { DAY_VIEW_COLUMN_HOUR_INTERVAL } from '../../../../common/constants';
import { getTodayColumnIndex } from '../../../../common/utils/columns.tsx';

export const getColumnMonthNumbers = memoizeOne((columnDurations: ColumnDuration[]) =>
	// Months are 0 indexed by date-fns
	columnDurations.map<number>((columnDuration) => getMonth(columnDuration.startDate)),
);

export const getColumnQuarterNumbers = memoizeOne((columnDurations: ColumnDuration[]) =>
	columnDurations.map<number>((columnDuration) => getQuarter(columnDuration.startDate) - 1),
);

// Year numbers will only be shown for columns that do not fall on the current year
export const getColumnYearCaptions = memoizeOne(
	(today: Date, columnDurations: ColumnDuration[]) => {
		const currentYear = getCurrentYear(today);
		return columnDurations.map<string | undefined>((columnDuration) => {
			const yearNumber = getYear(columnDuration.startDate);
			return currentYear !== yearNumber ? `'${yearNumber.toString().slice(2)}` : undefined;
		});
	},
);

// get numbers to display for day columns. We should 2 hour intervals for day view.
export const getColumnHours = memoizeOne((columnDurations: ColumnDuration[]) =>
	columnDurations.map<number[]>(() => {
		const hoursArray = [...new Array(24 / DAY_VIEW_COLUMN_HOUR_INTERVAL)];

		const dayNumbers: number[] = hoursArray.map((element, i) => i * DAY_VIEW_COLUMN_HOUR_INTERVAL);

		return dayNumbers;
	}),
);

// A week column is represented by the month that contains it, and the days it contains
export const getColumnWeekNumbers = memoizeOne((columnDurations: ColumnDuration[]) =>
	columnDurations.map<
		{
			day: number;
			month: number;
		}[]
	>((columnDuration) => {
		const { startDate } = columnDuration;
		const daysArray = [...new Array(7)];
		const weekNumbers: {
			day: number;
			month: number;
		}[] = daysArray.map((element, i) => {
			const columnMoment = addDays(startDate, i);
			return { day: getDate(columnMoment), month: getMonth(columnMoment) };
		});

		return weekNumbers;
	}),
);

// Get the [column, day] co-ordinates that represent today
export const getTodayCoordinates = memoizeOne((today: Date, columnDurations: ColumnDuration[]) => {
	let todayDayIndex = 0;
	const todayNumber = getDate(today);
	const todayColumnIndex = getTodayColumnIndex(today, columnDurations);
	const weekNumbers = getColumnWeekNumbers(columnDurations);

	weekNumbers[todayColumnIndex].forEach((weekNumber, index) => {
		if (weekNumber.day === todayNumber) {
			todayDayIndex = index;
		}
	});
	return [todayColumnIndex, todayDayIndex] as const;
});

export const isDateWithinTimelineDuration = (
	date: Date,
	timelineDuration: TimelineDuration,
): boolean => date >= timelineDuration.startDate && date <= timelineDuration.endDate;
